import * as React from 'react';
import { PrimaryButton } from '@fluentui/react';
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { MessageBar, MessageBarIntent, MessageBarActions, MessageBarBody, MessageBarGroup, Field } from '@fluentui/react-components';
import { DismissRegular } from "@fluentui/react-icons";
import { Button} from '@fluentui/react-components';
import { Spinner } from '@fluentui/react-components';
import { Input, InputProps } from '@fluentui/react-components';
import { IProject } from '../../api/model/IProject';
import { Task } from '../../api/Task';
import { TimePicker, TimePickerProps, formatDateToTimeString } from "@fluentui/react-timepicker-compat";
import { acquireResourceId } from '../../service/resource.service';
import { isAlldayEvent, getWorkDays } from "../../api/GraphProvider";
import { DayOfWeek } from '../../api/model/IWorkingHours';
import { dismissMessage, showMessage, useGlobalState, Message } from '../../service/globalState.service';
import { checkPendingCategory } from '../../service/settingsState.service';
import { getEventId } from '../../service/appointment.service';
import {
  Combobox,
  ComboboxProps,
  useComboboxFilter,
} from "@fluentui/react-components";


const { useState, useEffect } = React;

interface CreateTaskProps {
  onCreateSuccess: () => void;
  projects: IProject[];
}

export const CreateTask: React.FC<CreateTaskProps> = ({ onCreateSuccess, projects }) => {
  const [message] = useGlobalState('message');
  const [name, setName] = useState<string | undefined>();
  const [date, setDate] = useState<Date>();
  const [time, setTime] = useState<Date>(new Date());
  const [work, setWork] = useState<string | undefined>('');
  const [spinner, showSpinner] = useState<boolean>(false);
  const [selectedProject, setSelectedProject] = useState<IProject>();

  useEffect(() => {
    fetchResource();
    parseTimes();
  }, [])

  useEffect(() => {
    const { item } = Office.context.mailbox;
    item.subject.getAsync(result => parseSubject(result.value));
  }, [])

  
  useEffect(() => {
    if (name && projects.length > 0) {
      parseSubject(name);
    }
  }, [name, projects]);


  const resourceId = React.useRef<string>();
  const fetchResource = async () => {
    resourceId.current = await acquireResourceId();
  }

  const parseSubject = (subject: string) => {
    if (!subject) return;

    const [projectName, taskName] = subject.split(/ -(.*)/);
    const foundProject = projects.find(p => p.Name.toLowerCase() === projectName.trim().toLowerCase());
    if (!foundProject) {
      setName(subject);
      return;
    }

    setSelectedProject(foundProject);
    setQuery(foundProject.Name); // <-- add this
    if (taskName)
      setName(taskName.trim());
  }

  const checkIsAllDay = async (start: Date, end: Date): Promise<boolean> => {
    try {
      return await isAlldayEvent(await getEventId())
    }
    catch {
      return start.toTimeString().includes("00:00:00") && end.toTimeString().includes("00:00:00")
    }
  }

  const parseTimes = () => {
    const { item } = Office.context.mailbox;

    if (!item.start) return;

    item.start.getAsync(res => {
      const start = res.value;
      console.log(start);
      if (!start) return;
      setDate(new Date(start));
      setTime(new Date(start));
      item.end.getAsync(async r => {
        const end = r.value;
        if (!end) return;

        const msDiff = Math.abs(end.getTime() - start.getTime());
        const hours = msDiff / 36e5;
        const roundedHours = Math.round((hours + Number.EPSILON) * 100) / 100
        const isAllDay = await checkIsAllDay(start, end);
        if (!isAllDay)
          setWork(String(roundedHours))
        else {
          const tempDate = new Date(start);
          const weekday: DayOfWeek[] = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
          const workDays = await getWorkDays();
          let countedDays = 0;
          for (let i = 0; i < Math.ceil(msDiff / 864e5); i++) {
            if (workDays.daysOfWeek.includes(weekday[tempDate.getDay()])) {
              countedDays++
            }
            tempDate.setDate(tempDate.getDate() + 1)
          }
          setWork(String(countedDays * 8))
        }
      })
    })
  }

  const projectOptions = projects.map(p => ({
    value: p.Name,
    children: p.Name,
    data: p,
  }));

  const [query, setQuery] = React.useState<string>("");
  // If you need filtered options, use the result of useComboboxFilter
  const filteredOptions = useComboboxFilter(query, projectOptions, {
    noOptionsMessage: "No projects match your search.",
  });


  const onOptionSelect: ComboboxProps["onOptionSelect"] = (_, data) => {
    setQuery(data.optionText ?? ""); // show name in input
    const selected = projects.find(p => p.Name === data.optionText);
    if (selected) setSelectedProject(selected);
  };


  const create = async () => {
    showSpinner(true);

    try {
      const workHours = Math.abs(Number(work));
      const workTotal = Math.round(workHours * 60);
      const z = new Date().getTimezoneOffset() * 60 * 1000;

      const startDate = new Date(time.getTime() - z);
      startDate.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());



      const taskId = await Task.create({
        Name: name,
        ProjectId: selectedProject.Id,
        StartDate: startDate.toISOString(),
        ResourceId: resourceId.current,
        Work: workTotal
      });


      const successMessage: Message = {
        type: 'success',
        text: `Task ${name} in project ${selectedProject.Name} successfully created!`,
      };
      showMessage(successMessage);
      resetState();
      onCreateSuccess();
      const applyCategory = checkPendingCategory();
      if(applyCategory) {
        const { item } = Office.context.mailbox;
        item.categories.addAsync(["Pending"]);
        item.body.prependAsync(`
          <p>
          You have already requested an Allocatus Task for this event. Here some details about your request:<br/>
            <br/>
            Request date: ${new Date().toLocaleString()}<br/>
            Project: ${selectedProject.Name}<br/>
            Duration: ${workTotal / 60}h<br/>
          </p>
          `,
          { coercionType: Office.CoercionType.Html }
        )
      }
    } catch (e) {
      showMessage({
        type: "error",
        text: JSON.stringify(e),
      })
    }

    showSpinner(false);
  }

  const resetState = () => {
    setName('');
    setDate(new Date());
    setTime(new Date());
    setWork('');
  }

  console.log('>>>>>TIME: ', time)
  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', marginTop: '12px', gap: '8px', width: '120%' }}>
      <Field label="Name">
        <Input
          required
          value={name ?? ''}
          onChange={(_, data) => setName(data.value)}
          style={{ width: '100%' }}
        />
      </Field>

      <Field label="Project">
          <Combobox
            placeholder="Select a project"
            required
            onOptionSelect={onOptionSelect}
            onChange={(ev) => setQuery(ev.target.value)}
            value={query}
            style={{ width: '100%', minWidth: 0, flex: '1 1 auto' }}
            positioning={{
              position: "below",
              pinned: true,        
            }}
          >
          {filteredOptions}
          </Combobox>
      </Field>
      <Field label="Start Date">
        <DatePicker
          value={date || new Date()}
          onSelectDate={(v) => setDate(v)}
          style={{ width: '100%' }}
        />
      </Field>

      <Field label="Start Time">
        <TimePicker
          placeholder="Start"
          required
          increment={30}
          hourCycle="h12"
          selectedTime={time}
          value={formatDateToTimeString(time)}
          onTimeChange={(_, data) => setTime(data.selectedTime)}
          style={{ flex: 1, minWidth: 0 }}
        />
      </Field>

      <Field label="Work">
        <Input
          placeholder="Work"
          required
          type="number"
          contentAfter={<span >Hours</span>}
          value={work}
          onChange={(_, data) => setWork(data.value)}
          style={{ flex: 1, minWidth: 0 }}
        />
      </Field>

      <div style={{ display: 'flex', marginTop: '12px', gap: '12px', alignItems: 'center' }}>
        <PrimaryButton
          text="Create"
          disabled={!(name && selectedProject && work)}
          onClick={create}
        />
        {spinner && <Spinner />}
      </div>
    </div>

    </>
  );
}